/**
 * Grand Archive element icon assets + typed lookup maps.
 *
 * ASSET PROVENANCE: The 13 .png files in ./elements/ are the official 50×50px GA element
 * icons, sourced from the Silvie community CDN (img.silvie.org/misc/elements/<element>.png).
 * These images are Weebs of the Shore IP; bundling them was authorized by CD (D95) with the
 * associated third-party re-host IP/ToS risk accepted. The render path scales them to 20px.
 * To swap for vendor-supplied official assets later, replace the files in ./elements/ in
 * place (same filenames) — no code change required.
 *
 * GAElement is defined locally because the overlay cannot import from @sleeved/web.
 * It must stay in sync with the 13 canonical element names in colors.ts.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** 13 canonical GA element names — must match colors.ts keys exactly (titlecase). */
type GAElement =
  | 'Norm'
  | 'Fire'
  | 'Water'
  | 'Wind'
  | 'Arcane'
  | 'Astra'
  | 'Crux'
  | 'Exalted'
  | 'Exia'
  | 'Luxem'
  | 'Neos'
  | 'Tera'
  | 'Umbra';

// ---------------------------------------------------------------------------
// Static PNG imports (Vite resolves these to hashed CDN URLs at build time)
// vite/client provides the `declare module '*.png'` ambient declaration.
// ---------------------------------------------------------------------------

import normAsset    from './elements/norm.png';
import fireAsset    from './elements/fire.png';
import waterAsset   from './elements/water.png';
import windAsset    from './elements/wind.png';
import arcaneAsset  from './elements/arcane.png';
import astraAsset   from './elements/astra.png';
import cruxAsset    from './elements/crux.png';
import exaltedAsset from './elements/exalted.png';
import exiaAsset    from './elements/exia.png';
import luxemAsset   from './elements/luxem.png';
import neosAsset    from './elements/neos.png';
import teraAsset    from './elements/tera.png';
import umbraAsset   from './elements/umbra.png';

// ---------------------------------------------------------------------------
// Icon map
// satisfies enforces all 13 keys are present and that values are strings;
// literals are preserved for inference. Do NOT use Record<string, string> + as const.
// ---------------------------------------------------------------------------

export const GA_ELEMENT_ICON_MAP = {
  // Norm: official icon is a plain neutral circle (the element has no distinct glyph by design).
  Norm:    normAsset,
  Fire:    fireAsset,
  Water:   waterAsset,
  Wind:    windAsset,
  Arcane:  arcaneAsset,
  Astra:   astraAsset,
  Crux:    cruxAsset,
  Exalted: exaltedAsset,
  Exia:    exiaAsset,
  Luxem:   luxemAsset,
  Neos:    neosAsset,
  Tera:    teraAsset,
  Umbra:   umbraAsset,
} satisfies Record<GAElement, string>;

// ---------------------------------------------------------------------------
// Type guard
// entry.label is string; GA_ELEMENT_ICON_MAP is Record<GAElement, string>.
// Direct indexing is a type error, and a casing mismatch yields
// <img src={undefined}> which does NOT fire onError (blank cell, no fallback).
// Phase 1 normalizes labels to canonical titlecase; this guard is the runtime
// safety net in Phase 3 rendering.
// ---------------------------------------------------------------------------

export function isGAElement(label: string): label is GAElement {
  return label in GA_ELEMENT_ICON_MAP;
}
